import { Formik } from "formik"
import React, { useState } from "react"
import { MdSend } from "react-icons/md"
import * as yup from "yup"
import { useTurnstile } from "../hooks/use-turnstile"
import { ErrorAlert } from "./form/error-alert"
import {
  FormActions,
  PrimarySubmitButton,
  SecureField,
  SecureForm,
  VerificationRow,
  VerificationWidget,
} from "./form/secure-form"

const validationSchema = yup.object({
  email: yup.string().email("Invalid email").required("Email is required"),
  message: yup.string().required("Message is required"),
})

export const ContactForm = ({ name = "contact", topContent = () => {} }) => {
  const [isSuccess, setSuccess] = useState(false)
  const [isError, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState(
    "There was an error sending your message. Please try again later."
  )
  const { turnstileContainerId, turnstileToken, resetTurnstile } = useTurnstile(
    {
      containerPrefix: "contact-turnstile-widget",
      onVerify: () => {
        setError(false)
      },
      onError: (message) => {
        setErrorMessage(message)
        setError(true)
      },
      onExpired: (message) => {
        setErrorMessage(message)
        setError(true)
      },
    }
  )

  const onSubmit = async (values, { setSubmitting }) => {
    setError(false)
    setErrorMessage(
      "There was an error sending your message. Please try again later."
    )

    try {
      if (!turnstileToken) {
        setErrorMessage("Please complete the verification and try again.")
        setError(true)
        return
      }

      const response = await fetch("/contact-submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: values.email,
          message: values.message,
          botField: values["bot-field"] ?? "",
          turnstileToken,
        }),
      })

      let json
      try {
        json = await response.json()
      } catch (e) {
        json = null
      }

      if (!response.ok || !json || !json.ok) {
        const msg =
          (json && typeof json.error === "string" && json.error) ??
          "There was an error sending your message. Please try again later."
        setErrorMessage(msg)
        setError(true)
        return
      }

      setSuccess(true)
    } catch (err) {
      console.error(err)
      setError(true)
    } finally {
      resetTurnstile()
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={{
        email: "",
        message: "",
        "bot-field": "",
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, values, handleChange }) => (
        <>
          {topContent({ values }, { isError, isSuccess })}

          {!isSuccess && (
            <>
              <ErrorAlert
                show={isError}
                onClose={() => setError(false)}
                message={errorMessage}
              />

              <SecureForm
                name={name}
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                noValidate
              >
                <input type="hidden" name="form-name" value={name} />
                <input
                  type="hidden"
                  name="subject"
                  value="Speaking invitation from Evidence Strong"
                  readOnly
                />
                <input
                  type="hidden"
                  name="cf-turnstile-response"
                  value={turnstileToken}
                  readOnly
                />
                <div className="hidden-field" aria-hidden="true">
                  <label htmlFor="bot-field">
                    Don&apos;t fill this out if you&apos;re human.
                  </label>
                  <input
                    id="bot-field"
                    name="bot-field"
                    tabIndex="-1"
                    autoComplete="off"
                    value={values["bot-field"]}
                    onChange={handleChange}
                  />
                </div>
                <SecureField name="email" type="email" label="Email" />
                <SecureField
                  name="message"
                  label="Message"
                  component="textarea"
                  placeholder="Tell me about your gym, location, topic, and preferred timing."
                />

                <VerificationRow>
                  <VerificationWidget>
                    <div id={turnstileContainerId} />
                  </VerificationWidget>
                  <FormActions>
                    <PrimarySubmitButton
                      type="submit"
                      icon={MdSend}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Send message"}
                    </PrimarySubmitButton>
                  </FormActions>
                </VerificationRow>
              </SecureForm>
            </>
          )}
        </>
      )}
    </Formik>
  )
}
